
import { useState, useEffect, useRef } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { cn } from "@/lib/utils";

// Testimonial data type
type Testimonial = {
  id: number;
  quote: string;
  name: string;
  title: string;
  image: string;
};

// Mock data - updated to include the new testimonial
const testimonials: Testimonial[] = [
  {
    id: 1,
    quote: "Tommy Zee has a way of combining his vast business experience with the real questions people eventually all ask. What is the purpose of life? Why don't I have lasting peace of mind even though my business is successful? Why is my conscience bothering me all the time? I have used his ideas with our clients many times. He has the formula.",
    name: "Henry Kutarna",
    title: "CEO / Kutarna Capital Corporation & Founder / The Catholic CEO",
    image: "/lovable-uploads/70633dea-a6d2-482a-9a3a-c56775be1d07.png"
  },
  {
    id: 2,
    quote: "Tommy Zee is a brilliant business man, a spiritual leader, and great coach.\n\nTommy Zee is that rare expert who can mesh your success with business intelligence and spiritual truths.\n\nDon't try to improve just part of your being. Work with Tommy to improve your entire life.",
    name: "Alan Weiss, PhD",
    title: "Author of The Consulting Bible and over 50 other major books",
    image: "/lovable-uploads/c4cb5a1a-b358-44bb-a872-7b010b4904b6.png"
  },
  {
    id: 3,
    quote: "What sets Tommy Zee apart is his rare ability to see the complete picture of human potential. In a sea of advisors and coaches, Tommy stands out in his understanding that true success flows from alignment of purpose, practice, and spirit. Tommy doesn't just fix your business problems; he awakens your deeper motivations and helps you build something authentic to your core values. His guidance has been transformative in both my business growth and personal well-being.",
    name: "Wojtek Nowak",
    title: "Managing Director, Corporate Finance, Tricon",
    image: "/lovable-uploads/cb12f370-5b79-4007-9580-46463f83cd58.png"
  }
];

export default function TestimonialSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);
  const touchStartX = useRef<number | null>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const currentTestimonial = testimonials[currentIndex];
  
  // Handle navigation
  const goToSlide = (index: number, slideDirection: 'left' | 'right') => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setDirection(slideDirection);
    
    // Reset animation state after transition completes
    setTimeout(() => {
      setCurrentIndex(index);
      setIsAnimating(false);
      setDirection(null);
    }, 500); // Match this with the CSS transition duration
  };
  
  const goToNext = () => {
    const nextIndex = (currentIndex + 1) % testimonials.length;
    goToSlide(nextIndex, 'right');
  };
  
  const goToPrev = () => {
    const prevIndex = (currentIndex - 1 + testimonials.length) % testimonials.length;
    goToSlide(prevIndex, 'left');
  };
  
  // Touch event handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    
    // If swipe distance is significant enough
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        // Swipe left, go next
        goToNext();
      } else {
        // Swipe right, go prev
        goToPrev();
      }
    }
    
    touchStartX.current = null;
  };
  
  // Auto-advance slides
  useEffect(() => {
    const startAutoPlay = () => {
      autoPlayRef.current = setInterval(() => {
        goToNext();
      }, 8000); // Change slide every 8 seconds
    };
    
    startAutoPlay();
    
    // Pause auto-play when user interacts with component
    const pauseAutoPlay = () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
    
    const resumeAutoPlay = () => {
      pauseAutoPlay();
      startAutoPlay();
    };
    
    if (containerRef.current) {
      containerRef.current.addEventListener('mouseenter', pauseAutoPlay);
      containerRef.current.addEventListener('mouseleave', resumeAutoPlay);
    }
    
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
      
      if (containerRef.current) {
        containerRef.current.removeEventListener('mouseenter', pauseAutoPlay);
        containerRef.current.removeEventListener('mouseleave', resumeAutoPlay);
      }
    };
  }, [currentIndex]);
  
  // Render indicators for each testimonial
  const renderIndicators = () => {
    return testimonials.map((_, index) => (
      <button
        key={index}
        className={cn(
          "w-2 h-2 rounded-full transition-all duration-300 mx-1",
          index === currentIndex ? "bg-charcoal/90 w-6" : "bg-charcoal/30 hover:bg-charcoal/50"
        )}
        onClick={() => goToSlide(index, index > currentIndex ? 'right' : 'left')}
        aria-label={`Go to testimonial ${index + 1}`}
      />
    ));
  };
  
  return (
    <section 
      className="py-16 bg-offwhite" 
      aria-label="Testimonials"
    >
      <div className="container-custom" ref={containerRef}>
        <div 
          className="relative max-w-3xl mx-auto"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Quote decorations */}
          <div className="absolute -top-6 -left-2 md:-left-6 text-cream/50 z-0">
            <Quote size={40} />
          </div>
          <div className="absolute -bottom-2 -right-2 md:-right-6 text-cream/50 z-0 transform rotate-180">
            <Quote size={40} />
          </div>
          
          {/* Main testimonial content with animations */}
          <div 
            className={cn(
              "bg-white rounded-sm shadow-sm p-6 md:p-8 relative z-10 transition-all duration-500",
              isAnimating && direction === 'right' && "animate-slide-left",
              isAnimating && direction === 'left' && "animate-slide-right"
            )}
          >
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-8">
              <div className="shrink-0">
                <Avatar className="w-20 h-20 border-2 border-cream/50 shadow-md">
                  <AvatarImage 
                    src={currentTestimonial.image} 
                    alt={currentTestimonial.name} 
                    className="object-cover"
                  />
                  <AvatarFallback className="text-xl bg-cream/30 text-charcoal">
                    {currentTestimonial.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              <div className="flex-1">
                <blockquote>
                  <p className="text-base md:text-lg text-charcoal/90 italic leading-relaxed mb-4">
                    "{currentTestimonial.quote}"
                  </p>
                  
                  <footer className="mt-3">
                    <cite className="not-italic">
                      <div className="font-serif text-lg text-charcoal">
                        {currentTestimonial.name}
                      </div>
                      <div className="text-softgray text-xs mt-1">
                        {currentTestimonial.title}
                      </div>
                    </cite>
                  </footer>
                </blockquote>
              </div>
            </div>
          </div>
          
          {/* Navigation arrows */}
          <div className="flex justify-between absolute top-1/2 left-0 right-0 -translate-y-1/2 z-20 px-4">
            <button 
              className="w-8 h-8 rounded-full bg-white/80 shadow-sm flex items-center justify-center text-charcoal/70 hover:text-charcoal hover:bg-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-charcoal/30"
              onClick={goToPrev}
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={16} />
            </button>
            
            <button 
              className="w-8 h-8 rounded-full bg-white/80 shadow-sm flex items-center justify-center text-charcoal/70 hover:text-charcoal hover:bg-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-charcoal/30"
              onClick={goToNext}
              aria-label="Next testimonial"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        
        {/* Indicator dots */}
        <div className="flex justify-center mt-6">
          {renderIndicators()}
        </div>
      </div>
    </section>
  );
}
